/**
 * usePauseDetection - Pause detection for voice conversation
 *
 * Manages a single timer: when startPauseTimer is called, a timeout is scheduled;
 * if clearPauseTimer or startPauseTimer is called again before it fires, the previous
 * timeout is cleared. When the timeout fires, onPauseDetected is invoked.
 * Used to detect when the user has stopped speaking so the system can ask the next question.
 *
 * @param {Object} options
 * @param {number} options.pauseDurationMs - Delay in ms after last transcript before firing
 * @param {Function} options.onPauseDetected - Callback when pause duration is reached
 * @returns {{ startPauseTimer: (durationMs?: number) => void, clearPauseTimer: () => void }}
 */
import { useRef, useCallback, useEffect } from 'react';

export function usePauseDetection({ pauseDurationMs = 2000, onPauseDetected }) {
  const pauseTimerRef = useRef(null);
  const onPauseDetectedRef = useRef(onPauseDetected);
  onPauseDetectedRef.current = onPauseDetected;

  const clearPauseTimer = useCallback(() => {
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
  }, []);

  const startPauseTimer = useCallback((durationMs) => {
    clearPauseTimer();
    const duration = durationMs ?? pauseDurationMs;
    pauseTimerRef.current = setTimeout(() => {
      pauseTimerRef.current = null;
      onPauseDetectedRef.current?.();
    }, duration);
  }, [pauseDurationMs, clearPauseTimer]);

  useEffect(() => {
    return clearPauseTimer;
  }, [clearPauseTimer]);

  return { startPauseTimer, clearPauseTimer };
}
